import { Link } from "react-router-dom";
import { ArrowRight, Camera, Lightning, TextAa, Export, ShieldCheck, Tag, CloudArrowUp, WifiSlash } from "@phosphor-icons/react";
import { MarketingFrame } from "../components/MarketingFrame";
import { USE_CASE_LINKS } from "../content/useCases";

const STEPS = [
  { n: "01", icon: Camera, title: "CAPTURE", body: "Open the camera, pick a session, shoot. One tap per photo, no menus." },
  { n: "02", icon: TextAa, title: "AUTO-RENAME + TAG", body: "Every shot is named in sequence and tagged with your base tags the instant it's taken." },
  { n: "03", icon: Export, title: "EXPORT EXIF-BAKED", body: "Files leave with name, sequence, tags and session baked into EXIF. Zero post-shoot cleanup." },
];

const PROOF = [
  { icon: ShieldCheck, text: "EXIF baked on every shot" },
  { icon: Tag, text: "Unlimited captures, free forever" },
  { icon: WifiSlash, text: "Works offline · installable PWA" },
  { icon: CloudArrowUp, text: "1-tap sync to AIImageSuite.pro" },
];

export default function LandingPage() {
  return (
    <MarketingFrame
      testid="landing-page"
      title="AIImageSuites Capture — Capture 500+ Organized, EXIF-Baked Photos in Minutes"
      description="Mobile capture companion for AIImageSuite.pro. Rename and tag product photos at the moment of capture with EXIF baked in. Free with Google sign-in; Cloud Sync $12/mo."
    >
      <section className="px-6 py-14 sm:py-20">
        <div className="max-w-5xl mx-auto w-full space-y-8">
          <p className="label-mono text-neutral-500" data-testid="landing-tagline">
            MOBILE CAPTURE COMPANION FOR AIIMAGESUITE.PRO
          </p>
          <h1
            className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl max-w-4xl"
            data-testid="landing-headline"
          >
            CAPTURE <span className="text-[#FF2A2A]">500+</span> ORGANIZED,
            EXIF-BAKED PHOTOS IN MINUTES.
          </h1>
          <p className="text-lg sm:text-xl font-mono text-neutral-700 max-w-2xl" data-testid="landing-subhead">
            Rename and tag at the moment of capture — not after. Built for e-commerce
            resellers, product photographers and dataset creators shooting high-volume batches.
          </p>

          <div className="flex flex-col items-start gap-3 pt-2">
            <Link
              to="/signin"
              data-testid="landing-cta"
              className="btn-flat text-base sm:text-lg px-6 py-4 inline-flex items-center gap-3"
              style={{ boxShadow: "6px 6px 0 0 #0A0A0A" }}
            >
              <Camera weight="bold" size={22} />
              Start Capturing
              <ArrowRight weight="bold" size={20} />
            </Link>
            <span className="label-mono text-neutral-600" data-testid="landing-cta-subtext">
              <Lightning size={11} weight="bold" className="inline -mt-0.5 mr-1" />
              No credit card required · Instant EXIF tagging · Free with Google sign-in
            </span>
          </div>
        </div>
      </section>

      <section className="border-t-2 border-black px-6 py-12" data-testid="landing-workflow">
        <div className="max-w-5xl mx-auto w-full">
          <p className="label-mono text-neutral-500 mb-6">HOW IT WORKS · 3 STEPS</p>
          <ol className="grid grid-cols-1 md:grid-cols-3 border-2 border-black divide-y-2 md:divide-y-0 md:divide-x-2 divide-black">
            {STEPS.map((s, i) => (
              <li key={s.n} className="p-6 relative" data-testid={`landing-step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <span className="font-display font-black text-4xl tracking-tighter text-neutral-300">{s.n}</span>
                  <s.icon size={32} weight="bold" className={i === 0 ? "text-[#FF2A2A]" : i === 2 ? "text-[#002FA7]" : ""} />
                </div>
                <h2 className="font-display font-bold tracking-tighter text-lg mt-4">{s.title}</h2>
                <p className="text-sm font-mono text-neutral-700 mt-2 leading-relaxed">{s.body}</p>
                {i < STEPS.length - 1 && (
                  <ArrowRight size={20} weight="bold" className="hidden md:block absolute -right-[11px] top-1/2 -translate-y-1/2 bg-white z-10" />
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-t-2 border-black px-6 py-8" data-testid="landing-usecases">
        <div className="max-w-5xl mx-auto w-full flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="label-mono text-neutral-500">BUILT FOR</span>
          {USE_CASE_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="font-display font-bold tracking-tighter text-sm hover:text-[#FF2A2A] inline-flex items-center gap-1" data-testid={`landing-usecase-${l.to.split("/").pop()}`}>
              {l.label} <ArrowRight size={12} weight="bold" />
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t-2 border-black px-6 py-12 bg-[#F8F9FA]" data-testid="landing-proof">
        <div className="max-w-5xl mx-auto w-full grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="space-y-4">
            <p className="label-mono text-neutral-500">TRUSTED BY</p>
            <p className="font-display font-black tracking-tighter text-2xl sm:text-3xl" data-testid="landing-trust-badge">
              PHOTOGRAPHERS, RESELLERS &amp; DATASET CREATORS WHO SHOOT IN BATCHES.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 font-mono text-sm">
              {PROOF.map((p, i) => (
                <li key={i} className="flex items-center gap-2" data-testid={`landing-proof-${i}`}>
                  <p.icon size={16} weight="bold" className="text-green-700 shrink-0" />
                  {p.text}
                </li>
              ))}
            </ul>
          </div>
          <div className="border-2 border-black bg-white p-5 min-w-[240px]" data-testid="landing-pricing-teaser">
            <p className="label-mono text-neutral-500">PRICING</p>
            <p className="font-display font-black tracking-tighter text-3xl mt-1">FREE</p>
            <p className="text-xs font-mono text-neutral-600">to capture, rename, tag &amp; export</p>
            <p className="font-display font-bold tracking-tighter text-xl mt-3">$12/MO</p>
            <p className="text-xs font-mono text-neutral-600">adds Cloud Sync to AIImageSuite.pro</p>
            <Link to="/pricing" className="btn-flat-outline mt-4 w-full inline-flex items-center justify-center gap-2 py-2 text-sm" data-testid="landing-pricing-link">
              See full pricing <ArrowRight size={14} weight="bold" />
            </Link>
            <Link to="/compare/capture-time-naming-vs-renaming" className="label-mono text-[10px] text-neutral-500 hover:text-black mt-3 inline-block" data-testid="landing-compare-link">
              WHY CAPTURE-TIME NAMING BEATS RENAMING →
            </Link>
          </div>
        </div>
      </section>
    </MarketingFrame>
  );
}
